import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  ExternalLink,
  Pause,
  Play,
  RefreshCw,
  Save,
  Search,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import type { CreatorSettings, MemberListItem } from "@/lib/types";
import { HealthBadge } from "@/components/HealthBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ConversationView } from "@/components/ConversationView";
import { ChatTranscript } from "@/components/ChatTranscript";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { dollars, intFmt, pct } from "@/lib/money";
import { relTime, relTimeLong } from "@/lib/time";
import { useToasts } from "@/components/ui/toast";

export function CreatorDetail() {
  const { creatorId = "" } = useParams();
  const detailQ = useQuery({
    queryKey: ["creator", creatorId],
    queryFn: () => api.creator(creatorId),
    enabled: !!creatorId,
  });

  if (!creatorId) return <Empty title="No creator selected." />;

  if (detailQ.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-9 w-1/3" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }
  if (detailQ.isError || !detailQ.data) {
    return (
      <Empty
        title="Creator not found"
        hint={detailQ.error instanceof ApiError ? detailQ.error.message : ""}
      />
    );
  }

  const c = detailQ.data;
  const status = c.latest_status;

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/creators"
          className="inline-flex items-center gap-1 text-2xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" /> creators
        </Link>
      </div>

      <header className="flex items-start gap-4">
        <Avatar className="h-12 w-12">
          <AvatarImage src={c.avatar_url ?? undefined} alt="" />
          <AvatarFallback className="text-sm">
            {c.handle.slice(0, 2).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-xl font-semibold">@{c.handle}</h1>
            <Badge tone={c.status === "active" ? "success" : c.status === "paused" ? "warning" : "danger"}>
              {c.status}
            </Badge>
            {c.approval_mode === "supervised" ? (
              <Badge tone="info">supervised</Badge>
            ) : null}
            <HealthBadge health={c.health} reasons={c.health_reasons} />
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-xs text-muted-foreground">
            <span>{c.display_name}</span>
            <span>·</span>
            <span className="font-mono">{c.creator_id}</span>
            {c.paused_until ? (
              <>
                <span>·</span>
                <span className="text-warning">paused until {relTimeLong(c.paused_until)}</span>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PauseToggle creatorId={c.creator_id} pausedUntil={c.paused_until} />
          <Button
            variant="outline"
            size="iconSm"
            onClick={() => detailQ.refetch()}
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Stat label="Members 7d" value={intFmt(c.members_active_7d)} />
        <Stat label="Sends 7d" value={intFmt(c.sends_7d)} />
        <Stat label="Revenue 7d" value={dollars(c.revenue_7d_cents)} />
        <Stat label="Conversion" value={pct(c.conversion_7d_pct)} />
        <Stat label="Last report" value={relTime(c.last_self_report_at)} />
      </div>

      {status ? (
        <Card>
          <CardHeader>
            <CardTitle>Today's self-report</CardTitle>
            <CardDescription>
              {relTimeLong(status.reported_at)}
              {status.is_stale ? " · stale" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
              {status.summary || status.raw_text || "(no summary)"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Today's self-report</CardTitle>
            <CardDescription>Not yet received</CardDescription>
          </CardHeader>
          <CardContent>
            <Empty
              title="No report on file."
              hint="Self-reports arrive via morning prompt. Workers fall back to conservative voice while stale."
            />
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="kitt-chat">
        <TabsList>
          <TabsTrigger value="kitt-chat">Kitt chat</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="social">Social</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="memory">Memory</TabsTrigger>
        </TabsList>

        <TabsContent value="kitt-chat">
          <KittChatPanel creatorId={c.creator_id} />
        </TabsContent>
        <TabsContent value="members">
          <MembersPanel creatorId={c.creator_id} />
        </TabsContent>
        <TabsContent value="conversations">
          <ConversationsPanel creatorId={c.creator_id} />
        </TabsContent>
        <TabsContent value="social">
          <SocialPanel posts={c.recent_posts ?? []} />
        </TabsContent>
        <TabsContent value="settings">
          <SettingsPanel creatorId={c.creator_id} settings={c.settings} />
        </TabsContent>
        <TabsContent value="memory">
          <MemoryPanel
            creatorId={c.creator_id}
            memory={c.creator_memory}
            personaOverride={c.settings.persona_override_prompt}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="px-4 py-3">
        <div className="text-2xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="tnum text-lg font-semibold">{value}</div>
      </CardContent>
    </Card>
  );
}

function PauseToggle({ creatorId, pausedUntil }: { creatorId: string; pausedUntil: string | null }) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [hours, setHours] = React.useState("24");
  const [reason, setReason] = React.useState("");
  const [open, setOpen] = React.useState(false);

  const pause = useMutation({
    mutationFn: () => {
      const until = new Date(Date.now() + parseInt(hours, 10) * 3600_000).toISOString();
      return api.pauseCreator(creatorId, { until, reason: reason || "manual" });
    },
    onSuccess: () => {
      push({ tone: "warning", title: "Creator paused" });
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["creator", creatorId] });
    },
    onError: (e: unknown) =>
      push({
        tone: "danger",
        title: "Pause failed",
        description: (e as Error).message,
      }),
  });
  const unpause = useMutation({
    mutationFn: () => api.unpauseCreator(creatorId),
    onSuccess: () => {
      push({ tone: "success", title: "Unpaused" });
      qc.invalidateQueries({ queryKey: ["creator", creatorId] });
    },
  });

  if (pausedUntil) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => unpause.mutate()}
        disabled={unpause.isPending}
      >
        <Play className="mr-1.5 h-3 w-3" /> Unpause
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-2">
      {open ? (
        <div className="flex items-center gap-1.5 rounded-md border border-border bg-card p-1.5">
          <Input
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            className="h-7 w-14 text-xs"
            type="number"
            min={1}
          />
          <span className="text-xs text-muted-foreground">h</span>
          <Input
            placeholder="reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="h-7 w-40 text-xs"
          />
          <Button size="xs" onClick={() => pause.mutate()} disabled={pause.isPending}>
            confirm
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setOpen(false)}>
            cancel
          </Button>
        </div>
      ) : (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <Pause className="mr-1.5 h-3 w-3" /> Pause
        </Button>
      )}
    </div>
  );
}

/* ----------------- Members tab ----------------- */

function MembersPanel({ creatorId }: { creatorId: string }) {
  const [search, setSearch] = React.useState("");
  const [stage, setStage] = React.useState("all");
  const [openMember, setOpenMember] = React.useState<MemberListItem | null>(null);
  const q = useQuery({
    queryKey: ["members", creatorId, { stage }],
    queryFn: () =>
      api.members(creatorId, { stage: stage === "all" ? undefined : stage, limit: 500 }),
  });
  const items: MemberListItem[] = (q.data?.items ?? []).filter((m) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      m.phone_redacted.toLowerCase().includes(s) ||
      (m.display_handle ?? "").toLowerCase().includes(s) ||
      m.member_id.toLowerCase().includes(s)
    );
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Select value={stage} onValueChange={setStage}>
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">all stages</SelectItem>
            <SelectItem value="prospect">prospect</SelectItem>
            <SelectItem value="warming">warming</SelectItem>
            <SelectItem value="engaged">engaged</SelectItem>
            <SelectItem value="paid">paid</SelectItem>
            <SelectItem value="churned">churned</SelectItem>
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="phone or handle…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 w-56 pl-7 text-xs"
          />
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          {items.length} {items.length === 1 ? "member" : "members"}
        </span>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card/40">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Phone</TableHead>
              <TableHead>Handle</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead className="text-right">Trust</TableHead>
              <TableHead className="text-right">Detection</TableHead>
              <TableHead className="text-right">LTR</TableHead>
              <TableHead className="text-right">Last in</TableHead>
              <TableHead className="text-right">Last out</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {q.isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 9 }).map((__, j) => (
                    <td key={j} className="px-3 py-3">
                      <Skeleton className="h-4 w-20" />
                    </td>
                  ))}
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <td colSpan={9} className="px-4 py-12">
                  <Empty title="No members." />
                </td>
              </TableRow>
            ) : (
              items.map((m) => (
                <TableRow
                  key={m.member_id}
                  className="cursor-pointer"
                  onClick={() => setOpenMember(m)}
                >
                  <TableCell className="font-mono text-xs">{m.phone_redacted}</TableCell>
                  <TableCell>{m.display_handle ?? "—"}</TableCell>
                  <TableCell>
                    <Badge tone="muted" className="capitalize">
                      {m.stage}
                    </Badge>
                  </TableCell>
                  <TableCell className="tnum text-right">{m.trust_score.toFixed(2)}</TableCell>
                  <TableCell className="tnum text-right">
                    <span
                      className={
                        m.detection_score > 0.6
                          ? "text-destructive"
                          : m.detection_score > 0.3
                            ? "text-warning"
                            : ""
                      }
                    >
                      {m.detection_score.toFixed(2)}
                    </span>
                  </TableCell>
                  <TableCell className="tnum text-right">
                    {dollars(m.lifetime_revenue_cents, { compact: true })}
                  </TableCell>
                  <TableCell className="tnum text-right text-muted-foreground">
                    {relTime(m.last_inbound_at)}
                  </TableCell>
                  <TableCell className="tnum text-right text-muted-foreground">
                    {relTime(m.last_outbound_at)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      tone={
                        m.status === "active"
                          ? "success"
                          : m.status === "silenced"
                            ? "warning"
                            : "danger"
                      }
                    >
                      {m.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {openMember && (
        <MemberDetailDialog
          creatorId={creatorId}
          member={openMember}
          onClose={() => setOpenMember(null)}
        />
      )}
    </div>
  );
}

function MemberDetailDialog({
  creatorId, member, onClose,
}: {
  creatorId: string;
  member: MemberListItem;
  onClose: () => void;
}) {
  const threadQ = useQuery({
    queryKey: ["thread", creatorId, member.member_id],
    queryFn: () =>
      api.thread(creatorId, member.member_id, { limit: 100 }),
    refetchInterval: 10_000,
  });
  const items = (threadQ.data?.items ?? []).slice().reverse();

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {member.display_handle || member.phone_redacted}
          </DialogTitle>
          <DialogDescription>
            {member.stage} · trust {member.trust_score.toFixed(2)} · detection{" "}
            <span className={member.detection_score > 0.5 ? "text-rose-600" : ""}>
              {member.detection_score.toFixed(2)}
            </span>
            {" · "}LTR {dollars(member.lifetime_revenue_cents, { compact: true })}
          </DialogDescription>
        </DialogHeader>

        <section className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Stage" value={member.stage} />
            <Stat label="Last in" value={relTime(member.last_inbound_at)} />
            <Stat label="Last out" value={relTime(member.last_outbound_at)} />
          </div>
          <div>
            <h3 className="text-sm font-medium mb-2">Conversation history</h3>
            <ChatTranscript
              loading={threadQ.isLoading}
              items={items.map((m) => ({
                id: m.id,
                direction: m.from_member ? "inbound" : "outbound",
                label: m.from_member ? "member" : "creator",
                tag: m.kind,
                timestamp: m.sent_at,
                text: m.text,
              }))}
              heightClass="max-h-[500px]"
              emptyTitle="No messages logged"
              emptyHint="Once messages flow on this thread, they'll appear here."
            />
          </div>
        </section>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------- Conversations tab ----------------- */

function ConversationsPanel({ creatorId }: { creatorId: string }) {
  const q = useQuery({
    queryKey: ["members", creatorId, { stage: "all" }],
    queryFn: () => api.members(creatorId, { limit: 200 }),
  });
  const [memberId, setMemberId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState("");
  const items = (q.data?.items ?? []).filter((m) =>
    !search
      ? true
      : (m.display_handle ?? m.phone_redacted).toLowerCase().includes(search.toLowerCase()),
  );
  React.useEffect(() => {
    if (!memberId && items[0]) setMemberId(items[0].member_id);
  }, [items, memberId]);

  return (
    <div className="grid grid-cols-[260px_1fr] gap-3 overflow-hidden rounded-lg border border-border bg-card/40">
      <div className="border-r border-border">
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="search members…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-7 text-xs"
            />
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto py-1">
          {q.isLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="px-2 py-2">
                <Skeleton className="h-7 w-full" />
              </div>
            ))
          ) : items.length === 0 ? (
            <div className="p-4">
              <Empty title="No members." />
            </div>
          ) : (
            items.map((m) => (
              <button
                type="button"
                key={m.member_id}
                onClick={() => setMemberId(m.member_id)}
                className={
                  "block w-full px-3 py-2 text-left text-xs transition-colors hover:bg-accent " +
                  (memberId === m.member_id ? "bg-accent" : "")
                }
              >
                <div className="truncate font-mono">{m.phone_redacted}</div>
                <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
                  <span className="capitalize">{m.stage}</span>
                  <span>·</span>
                  <span>{relTime(m.last_inbound_at)}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
      <div className="min-w-0">
        {memberId ? (
          <ConversationView creatorId={creatorId} memberId={memberId} />
        ) : (
          <Empty title="Select a member" />
        )}
      </div>
    </div>
  );
}

/* ----------------- Social tab ----------------- */

function SocialPanel({ posts }: { posts: import("@/lib/types").SocialPost[] }) {
  if (!posts.length) {
    return (
      <Empty
        title="No social posts on file."
        hint="Configure IG/Twitter/YouTube handles in Settings to start polling."
      />
    );
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {posts.map((p) => (
        <Card key={p.id} className="overflow-hidden">
          {p.thumbnail_url ? (
            <a href={p.url} target="_blank" rel="noreferrer">
              <img
                src={p.thumbnail_url}
                alt=""
                className="aspect-square w-full object-cover"
                loading="lazy"
              />
            </a>
          ) : (
            <div className="aspect-square w-full bg-muted/40" />
          )}
          <div className="p-3">
            <div className="flex items-center gap-2 text-2xs uppercase tracking-wide text-muted-foreground">
              <span>{p.platform}</span>
              <span>·</span>
              <span>{relTime(p.posted_at)}</span>
              <a
                href={p.url}
                target="_blank"
                rel="noreferrer"
                className="ml-auto text-muted-foreground hover:text-foreground"
              >
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
            <p className="mt-1 line-clamp-2 text-xs">{p.caption}</p>
            {p.vision_tags.length ? (
              <div className="mt-2 flex flex-wrap gap-1">
                {p.vision_tags.slice(0, 6).map((t) => (
                  <Badge key={t} tone="muted" className="text-[10px]">
                    {t}
                  </Badge>
                ))}
              </div>
            ) : null}
          </div>
        </Card>
      ))}
    </div>
  );
}

/* ----------------- Settings tab ----------------- */

function SettingsPanel({
  creatorId,
  settings,
}: {
  creatorId: string;
  settings: CreatorSettings;
}) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [draft, setDraft] = React.useState<CreatorSettings>(settings);
  React.useEffect(() => setDraft(settings), [settings]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);

  const save = useMutation({
    mutationFn: () => api.patchCreator(creatorId, draft as unknown as Record<string, unknown>),
    onSuccess: () => {
      push({ tone: "success", title: "Saved" });
      qc.invalidateQueries({ queryKey: ["creator", creatorId] });
    },
    onError: (e: unknown) =>
      push({
        tone: "danger",
        title: "Save failed",
        description: (e as Error).message,
      }),
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Approval</CardTitle>
          <CardDescription>Who signs off on outbound messages.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label="Approval mode">
            <Select
              value={draft.approval_mode}
              onValueChange={(v) =>
                setDraft({ ...draft, approval_mode: v as CreatorSettings["approval_mode"] })
              }
            >
              <SelectTrigger className="text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="autonomous">autonomous</SelectItem>
                <SelectItem value="supervised">supervised</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <ToggleField
            label="Require human approval for paid DMs"
            checked={draft.requires_paid_approval}
            onChange={(v) => setDraft({ ...draft, requires_paid_approval: v })}
          />
          <Field label="Paused until">
            <Input
              type="datetime-local"
              value={draft.paused_until ? draft.paused_until.slice(0, 16) : ""}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  paused_until: e.target.value ? new Date(e.target.value).toISOString() : null,
                })
              }
            />
          </Field>
          <Field label="Morning prompt time (local)">
            <Input
              type="time"
              value={draft.morning_prompt_local_time ?? ""}
              onChange={(e) => setDraft({ ...draft, morning_prompt_local_time: e.target.value })}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Social handles</CardTitle>
          <CardDescription>Drive context-enrichment polling.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Field label="Instagram">
            <Input
              value={draft.ig_handle ?? ""}
              onChange={(e) => setDraft({ ...draft, ig_handle: e.target.value })}
              placeholder="@handle"
            />
          </Field>
          <Field label="Twitter / X">
            <Input
              value={draft.twitter_handle ?? ""}
              onChange={(e) => setDraft({ ...draft, twitter_handle: e.target.value })}
              placeholder="@handle"
            />
          </Field>
          <Field label="YouTube channel">
            <Input
              value={draft.youtube_channel ?? ""}
              onChange={(e) => setDraft({ ...draft, youtube_channel: e.target.value })}
              placeholder="@channel"
            />
          </Field>
          <Field label="IG poll cadence (minutes)">
            <Input
              type="number"
              min={5}
              value={draft.ig_poll_cadence_minutes ?? 60}
              onChange={(e) =>
                setDraft({ ...draft, ig_poll_cadence_minutes: Number(e.target.value) })
              }
            />
          </Field>
        </CardContent>
      </Card>

      <Card className="md:col-span-2">
        <CardHeader>
          <CardTitle>Persona override</CardTitle>
          <CardDescription>
            Free-form prompt prepended to the creator's persona. Use sparingly.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            rows={6}
            value={draft.persona_override_prompt ?? ""}
            onChange={(e) => setDraft({ ...draft, persona_override_prompt: e.target.value })}
            placeholder="(empty = no override)"
            className="font-mono text-xs"
          />
        </CardContent>
      </Card>

      <div className="md:col-span-2 flex items-center justify-end gap-2">
        <Button
          variant="ghost"
          size="sm"
          disabled={!dirty}
          onClick={() => setDraft(settings)}
        >
          discard
        </Button>
        <Button size="sm" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          <Save className="mr-1.5 h-3 w-3" />
          {save.isPending ? "saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function ToggleField({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <Label className="cursor-pointer text-xs normal-case">{label}</Label>
      <Switch checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

/* ----------------- Memory tab ----------------- */

function MemoryPanel({
  creatorId,
  memory,
  personaOverride,
}: {
  creatorId: string;
  memory: Record<string, unknown>;
  personaOverride: string | null;
}) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [override, setOverride] = React.useState(personaOverride ?? "");
  React.useEffect(() => setOverride(personaOverride ?? ""), [personaOverride]);
  const save = useMutation({
    mutationFn: () => api.patchCreator(creatorId, { persona_override_prompt: override }),
    onSuccess: () => {
      push({ tone: "success", title: "Persona override saved" });
      qc.invalidateQueries({ queryKey: ["creator", creatorId] });
    },
  });

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>creator_memory</CardTitle>
          <CardDescription>Read-only. Maintained by workers.</CardDescription>
        </CardHeader>
        <CardContent>
          <pre className="max-h-[60vh] overflow-auto rounded-md bg-muted/40 p-3 font-mono text-xs">
            {JSON.stringify(memory ?? {}, null, 2)}
          </pre>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Persona override</CardTitle>
          <CardDescription>Editable from here too — convenience.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            rows={12}
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            className="font-mono text-xs"
          />
          <Button
            size="sm"
            disabled={save.isPending || override === (personaOverride ?? "")}
            onClick={() => save.mutate()}
          >
            <Save className="mr-1.5 h-3 w-3" />
            Save override
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function KittChatPanel({ creatorId }: { creatorId: string }) {
  const q = useQuery({
    queryKey: ["creator-kitt-chat", creatorId],
    queryFn: () => api.creatorChatLog(creatorId, { limit: 200 }),
    refetchInterval: 8_000,
  });
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-sm font-medium">Kitt ↔ Creator iMessage</h3>
            <p className="text-xs text-muted-foreground">
              Auto-refreshing. Outbound = Kitt (blue, left). Inbound = creator (gray, right).
            </p>
          </div>
        </div>
        <ChatTranscript
          items={(q.data?.items ?? []).map((m) => ({
            id: m.id,
            direction: m.direction,
            label: m.direction === "outbound" ? "Kitt" : "creator",
            tag: m.ai_intent,
            timestamp: m.message_timestamp,
            text: m.message_text,
          }))}
          loading={q.isLoading}
          heightClass="max-h-[600px]"
          emptyTitle="No messages yet"
          emptyHint="Once the creator texts Kitts iMessage, the thread will appear here."
        />
      </CardContent>
    </Card>
  );
}

